import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../../hooks/use-logged-user';
import usePageViewEvent from '../../../../hooks/use-page-view-event';
import { translateSlugs } from '../../../../services/language/slug_translation';
import useHreflang from '../../../../utils/seo/hreflang';
import useOGMeta from '../../../../utils/seo/ogmeta';
import useSchemaBreadcrumbs from '../../../../utils/seo/schema-org/breadcrumbs';
import useSchema from '../../../../utils/seo/schema-org/schema';
import useSchemaWebPage from '../../../../utils/seo/schema-org/webpage';
import { useAppContext } from '../../../providers/app-context';

const CategoriesSchema: FunctionComponent<{
  carInfo: any;
  breadcrumbs: Array<{
    href: LinkProps;
    text: string;
  }>;
  translatedSlugs: any;
  count: number;
}> = ({ carInfo, breadcrumbs, translatedSlugs, count }) => {
  const { locale = 'en', asPath, pathname, query } = useRouter();
  const { t } = useTranslation();
  const user = useLoggedUser();
  const { businessValues } = useAppContext();
  const localePath = locale !== 'en' ? `/${locale}` : '';

  const name = carInfo
    ? t('categories:head.with-vehicle.title', {
        vehicle: `${carInfo.brand.name || ''} ${carInfo.model?.name || ''} ${
          carInfo.version?.name || ''
        }`,
      })
    : t('categories:head.no-vehicle.title', {
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      });

  const description = carInfo
    ? t('categories:head.with-vehicle.description', {
        vehicle: `${carInfo.brand.name || ''} ${carInfo.model?.name || ''} ${
          carInfo.version?.name || ''
        }`,
        count: count.toLocaleString(locale),
      })
    : t('categories:head.no-vehicle.description', {
        count: count.toLocaleString(locale),
        totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
      });

  const og = useOGMeta({
    title: name,
    description: description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
  });

  const alternateLinks = useHreflang({
    locale,
    route: pathname.includes('[category]')
      ? pathname.includes('[slug]')
        ? 'sub-categories-with-vehicle'
        : 'categories-with-vehicle'
      : 'categories',
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  usePageViewEvent(name);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" key="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default CategoriesSchema;
